import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import {
  ApplyPageStyleError,
  CollectionType,
  Common,
  DetectionType,
  NumBool,
  PageConstants,
  PageDetectionActionType,
  PageStyleType,
  ViewMode,
} from '@taopix/taopix-core';
import {
  ComputedPageItem,
  ExperienceType,
  PageStyleProps,
  ProductLayout,
} from '@taopix/taopix-core/dist/types/types';
import {
  ArrowLeftTopIcon,
  BackgroundAllLeftIcon,
  BackgroundAllRightIcon,
  Button,
  ButtonGrid,
  ButtonWithPopout,
  CheckCircleIcon,
  CloseIcon,
  Heading,
  HelpTip,
  Horizontal,
  InfoIcon,
  LayoutIcon,
  LightBulbIcon,
  MoreIcon,
  PopOut,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {HelpTipProps} from '@taopix/taopix-design-system/dist/types/Components/HelpTip/HelpTip';
import {
  PopOutProps,
  popDirectionTypes,
} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {LayoutSelector, PageListSelector, TotalPageCountSelector} from '../../Selectors';
import {filterObject} from '../../Utils/Common';
import {useTaopix} from '../Context/TaopixContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useExperience} from '../Experience/ExperienceProvider';
import LayoutsList from '../LayoutsList/LayoutsList';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import ApplyToAllWarningDialog from './Calendars/ApplyToAllWarningDialog';
import useFilter from './useFilter';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import TabList from '../Sidebar/Tablist/TabList';
import useKeyboardTipOnFocus from '../../Hooks/useKeyboardTipOnFocus';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';

export type LayoutsPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
  viewMode: ViewMode;
};

type ApplyOptionsState = {
  applyToAllPagesButtonLabel: string;
  applyToAllPagesButton: boolean;
  applyToAllPagesPopOut: boolean;
};

type FilterCount = {
  pictureFilterCount: number;
  categoryFilterCount: number;
  typeFilterCount: number;
};

type SelectedFilterButton = {
  type: string;
  numberOfPicBoxes: string;
  categories: string;
};

type PageStyleReduced = {
  type: number;
  label: string;
  value: string;
  key: string;
};

enum LayoutTypeTab {
  None = -1,
  Cover = 0,
  Page = 1,
}

const StaticFilters = {
  All: [{emptypictureboxcount: {}}, {textboxcount: {}}],
  AllCover: [{emptypictureboxcount: {}}, {textboxcount: {}}, {categoryName: {}}, {type: 2}],
  AllPages: [{emptypictureboxcount: {}}, {textboxcount: {}}, {categoryName: {}}, {type: {ne: 2}}],
  AllPagesSingle: [{emptypictureboxcount: {}}, {textboxcount: {}}, {categoryName: {}}, {type: 0}],
  TextOnly: [{emptypictureboxcount: 0}, {textboxcount: {gt: 0}}],
  PictureOnly: [{emptypictureboxcount: {gt: 0}}, {textboxcount: 0}],
  TextAndPicture: [{emptypictureboxcount: {gt: 0}}, {textboxcount: {gt: 0}}],
  Cover: [{type: 2}],
  Page: [{type: {ne: 2}}],
  SinglePages: [{type: 0}],
  SpreadPages: [{type: 1}],
  SingleAndSpread: [{type: {lt: 2}}],
};

const LayoutsPalette = React.forwardRef(
  (
    {sectionKey, viewMode, item}: LayoutsPaletteProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const app = useTaopix();
    const ui = useUiState();
    const userFeedback = useUserFeedback();
    const pageNumberList = useSelector(PageListSelector);
    const contentRef = useRef<HTMLDivElement>(null);

    const {t} = useTranslation(['toolpalette', 'helptips']);

    const {experience, updateExperience} = useExperience();

    // get the layout permission from experience
    const perms = experience.EDITOR.layouts;

    const defaultPageStyle: Readonly<PageStyleProps> = {
      ref: '-1',
      type: PageStyleType.None,
      categoryName: '',
      emptypictureboxcount: 0,
      hasgrid: 0,
      thumbnailPath: '',
      thumbnailRatio: 0,
      excludeFromAutoflow: 1,
      textboxcount: 0,
      portraitPictureBoxCount: 0,
      landscapePictureBoxCount: 0,
      squarePictureBoxCount: 0,
      detection: DetectionType.PageStyle,
      name: '',
      alternativeRef: 0,
    };

    const [filter, setFilter] = useFilter<PageStyleProps>();
    const [applyToAllPopoutOpen, setApplyToAllPopoutOpen] = useState(false);
    const [layoutFilterPopOutOpen, setLayoutFilterPopOutOpen] = useState<boolean>(false);
    const [isNonSpreadProduct, setIsNonSpreadProduct] = useState<boolean>(false);
    const [isSpreadProduct, setIsSpreadProduct] = useState<boolean>(false);
    const [selectedButtonIndex, setSelectedButtonIndex] = useState<number>(LayoutTypeTab.None);
    const [selectedLayout, setSelectedLayout] = useState<PageStyleProps>(defaultPageStyle);
    const [previousPage, setPreviousPage] = useState<number>(ui.state.pageNumber);
    const [selectedFilterButtons, setSelectedFilterButtons] = useState<SelectedFilterButton>({
      type: '',
      numberOfPicBoxes: '',
      categories: '',
    });
    const [applyOptionsState, setApplyOptionsState] = useState<ApplyOptionsState>({
      applyToAllPagesButtonLabel: t('*:str_ButtonApplyToAllPagesEllipsis'),
      applyToAllPagesButton: true,
      applyToAllPagesPopOut: true,
    });
    const [filterCountState, setFilterCountState] = useState<FilterCount>({
      pictureFilterCount: 0,
      categoryFilterCount: 0,
      typeFilterCount: 0,
    });

    const paletteOpen = ui.state.palette.section?.includes(sectionKey) || false;
    const [introTipOpen, setIntroTipOpen] = useState<boolean>(false);
    const [loaded, setLoaded] = useState(false);
    const applyButtonDisabled = selectedLayout.ref === defaultPageStyle.ref;
    const canDeviceDrag = matchMedia('(pointer: fine)').matches;
    const productLayout = useSelector(LayoutSelector);

    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const applyToAllButtonRef = useRef<HTMLButtonElement>(null);

    const statePageNumberRef = useRef(ui.state.pageNumber);
    useEffect(() => {
      statePageNumberRef.current = ui.state.pageNumber;
    }, [ui.state.pageNumber]);

    useEffect(() => {
      if (ui.state.palette.section === 'layouts' && item?.ref !== -1) {
        app.core.dispatch('SHAPE.DESELECT', null).catch(error => {
          console.error(error);
        });
      }
    }, [ui.state.palette.section]);

    const totalFilterCount =
      filterCountState.categoryFilterCount +
      filterCountState.pictureFilterCount +
      filterCountState.typeFilterCount;

    const pagestyles = productLayout?.pagestyles || [];
    const filteredPageStyles = pagestyles.filter(p => filterObject(p, filter));

    const pageTypes =
      pagestyles.reduce(
        (accumulator: {[key: number]: PageStyleReduced}, pagestyle: PageStyleProps) => {
          if (!accumulator[pagestyle.type]) {
            accumulator[pagestyle.type] = {
              type: pagestyle.type,
              label: `${pagestyle.type}`,
              value: `${pagestyle.type}`,
              key: `${Object.keys(accumulator).length}`,
            };
          }
          return accumulator;
        },
        {}
      ) || {};

    const picCounts = useMemo(() => {
      return (
        filteredPageStyles.reduce(
          (accumulator: {[key: number]: PageStyleReduced}, pagestyle: PageStyleProps) => {
            if (!accumulator[pagestyle.emptypictureboxcount]) {
              accumulator[pagestyle.emptypictureboxcount] = {
                type: pagestyle.type,
                label: `${pagestyle.emptypictureboxcount} ${
                  pagestyle.emptypictureboxcount === 1
                    ? t('*:str_LabelPicture')
                    : t('*:str_LabelPictures')
                }`,
                value: `${pagestyle.emptypictureboxcount}`,
                key: `${Object.keys(accumulator).length}`,
              };
            }
            return accumulator;
          },
          {}
        ) || {}
      );
    }, [selectedButtonIndex, pagestyles]);

    const allPicCounts = useMemo(() => {
      return (
        pagestyles.reduce(
          (accumulator: {[key: number]: PageStyleReduced}, pagestyle: PageStyleProps) => {
            if (!accumulator[pagestyle.emptypictureboxcount]) {
              accumulator[pagestyle.emptypictureboxcount] = {
                type: pagestyle.type,
                label: `${pagestyle.emptypictureboxcount} ${
                  pagestyle.emptypictureboxcount === 1
                    ? t('*:str_LabelPicture')
                    : t('*:str_LabelPictures')
                }`,
                value: `${pagestyle.emptypictureboxcount}`,
                key: `${Object.keys(accumulator).length}`,
              };
            }
            return accumulator;
          },
          {}
        ) || {}
      );
    }, [selectedButtonIndex, pagestyles]);

    const setAllPicBoxBtn = (e: React.MouseEvent) => {
      setFilter([{emptypictureboxcount: {}}]);
      setSelectedFilterButtons({...selectedFilterButtons, numberOfPicBoxes: ''});
      setFilterCountState({
        ...filterCountState,
        pictureFilterCount: 0,
      });
    };

    const setAllCatBoxBtn = (e: React.MouseEvent) => {
      setFilter([{categoryName: {}}]);
      setSelectedFilterButtons({...selectedFilterButtons, categories: ''});
      setFilterCountState({
        ...filterCountState,
        categoryFilterCount: 0,
      });
    };

    const categories = useMemo(() => {
      return (
        filteredPageStyles.reduce(
          (accumulator: {[key: number]: PageStyleReduced}, pagestyle: PageStyleProps) => {
            const category = Common.getCurrentLocaleString(pagestyle.categoryName, true);
            if (category !== '' && !accumulator[category]) {
              accumulator[category] = {
                type: pagestyle.type,
                label: `${category}`,
                value: `${pagestyle.categoryName}`,
                key: `${Object.keys(accumulator).length}`,
              };
            }
            return accumulator;
          },
          {}
        ) || {}
      );
    }, [selectedButtonIndex, pagestyles]);

    const allCategories = useMemo(() => {
      return (
        pagestyles.reduce(
          (accumulator: {[key: number]: PageStyleReduced}, pagestyle: PageStyleProps) => {
            const category = Common.getCurrentLocaleString(pagestyle.categoryName, true);
            if (category !== '' && !accumulator[category]) {
              accumulator[category] = {
                type: pagestyle.type,
                label: `${category}`,
                value: `${pagestyle.categoryName}`,
                key: `${Object.keys(accumulator).length}`,
              };
            }
            return accumulator;
          },
          {}
        ) || {}
      );
    }, [selectedButtonIndex, pagestyles]);

    const toggleApplyToAllPopout = () => {
      setApplyToAllPopoutOpen(!applyToAllPopoutOpen);
    };

    const onApplyToAllLayouts = (
      layoutRef: string | number,
      detectionType: PageDetectionActionType,
      applyToAllString: string
    ) => {
      app.core
        .get('stateManager')
        .call('applyToAll', {
          type: 'layouts',
          ref: Number(layoutRef),
          pageDetection: detectionType,
        })
        .then(() => {
          setHelpTipState({
            ...helpTipState,
            open: true,
            message: t(applyToAllString, {ns: 'helptips'}),
            icon: <CheckCircleIcon />,
            buttonLabel: t('*:str_ButtonOK'),
            buttonFunction: closeHelpTip,
            showAfter: 500,
            hideAfter: 5000,
          });
          setSelectedLayout(defaultPageStyle);
          setApplyToAllPopoutOpen(false);
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        })
        .catch(error => {
          let message = app.core.get('server').getErrorFromResponse(error);
          if ('' === message || !message) {
            message = error;
          }
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message,
              showHeader: true,
            })
          );
        });
    };

    // if applying to single page we dont show popout and display message that the layout has been applied
    const applyToAll = () => {
      if (!isNonSpreadProduct && selectedLayout.type === PageStyleType.Single) {
        toggleApplyToAllPopout();
      } else {
        onApplyToAllLayouts(
          selectedLayout?.ref,
          isSpreadProduct ? PageDetectionActionType.AllBoth : PageDetectionActionType.AllLeft,
          isSpreadProduct
            ? 'str_MessageLayoutAppliedToAllSpreads'
            : 'str_MessageLayoutAppliedToAllPages'
        );
      }
    };

    // The popouts are anchored to their respective buttons on large screen, but anchored to the bottom of the Layouts palette on small screen
    const popoutProps: Partial<PopOutProps> = {
      ...(ui.state.isSmallScreen
        ? {
            offset: {x: 0, y: 0},
            anchorOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Bottom},
          }
        : {
            offset: {x: 0, y: -5},
            anchorOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Top},
          }),
      displayMode: 'positioned',
      repositionOnResize: true,
      transformOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Bottom},
      popDirection: 'up' as popDirectionTypes,
      afterClose: () => {
        if (ui.state.busy) {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        }
      },
    };

    const buttonCSS = 'mt-xs ml-xs';

    const setPictureBoxButton = (itemValue: string, e: React.MouseEvent) => {
      setFilter([{emptypictureboxcount: itemValue}]);
      setSelectedFilterButtons({...selectedFilterButtons, numberOfPicBoxes: itemValue});
      setFilterCountState({
        ...filterCountState,
        pictureFilterCount: 1,
      });
    };

    const setCategoryButton = (itemValue: string, e: React.MouseEvent) => {
      setFilter([{categoryName: itemValue}]);
      setSelectedFilterButtons({...selectedFilterButtons, categories: itemValue});
      setFilterCountState({
        ...filterCountState,
        categoryFilterCount: 1,
      });
    };

    const contentWrapperClasses = classNames(
      'flex',
      'lg:flex-1',
      'flex-col',
      'min-h-0',
      'sm:h-mobilePaletteContentHeight',
      'sm:mt-auto'
    );

    const tabsContainerClasses = classNames(
      'flex',
      'px-md border-b border-b-themeTextColour20 ',
      'mb-md',
      'lg:space-x-xs',
      'sm:space-x-sm'
    );

    const bottomButtonsContainerClasses = classNames(
      'flex',
      'p-md',
      'lg:w-full',
      'sm:w-[min(400rem,theme(width.bounds))]',
      'sm:self-center',
      'sm:space-x-sm'
    );

    const thumbnailScrollContainerClasses = classNames(
      'flex',
      'flex-col',
      'flex-1',
      'px-md',
      'pb-[60rem]',
      'overflow-y-auto'
    );

    const filtersPopoutClasses = classNames(
      ui.state.isSmallScreen ? 'sm:w-[min(600rem,theme(width.bounds))]' : 'w-[300rem]'
    );

    const applyToAllPopOutClasses = classNames(
      ui.state.isSmallScreen && 'pt-sm',
      'flex-col',
      'lg:w-[var(--anchorElementWidth)]',
      'sm:w-[min(600rem,theme(width.bounds))]'
    );

    const statePageNumber = ui.state.pageNumber;

    const onSelectCoverTab = () => {
      if (LayoutTypeTab.Cover !== selectedButtonIndex) {
        if (selectedLayout) {
          setSelectedLayout(defaultPageStyle);
        }
        setSelectedButtonIndex(LayoutTypeTab.Cover);
        setFilter(StaticFilters.AllCover);
        setSelectedFilterButtons({
          type: '',
          numberOfPicBoxes: '',
          categories: '',
        });

        setApplyOptionsState({
          ...applyOptionsState,
          applyToAllPagesButton: false,
        });
        setFilterCountState({
          categoryFilterCount: 0,
          typeFilterCount: 0,
          pictureFilterCount: 0,
        });
      }
      if (applyToAllPopoutOpen) {
        setApplyToAllPopoutOpen(!applyToAllPopoutOpen);
      }
    };

    const onSelectPageTab = () => {
      if (LayoutTypeTab.Page !== selectedButtonIndex) {
        if (selectedLayout) {
          setSelectedLayout(defaultPageStyle);
        }
        setSelectedButtonIndex(LayoutTypeTab.Page);
        if (NumBool.True === productLayout.spreads) {
          setFilter(StaticFilters.AllPages);
        } else {
          setFilter(StaticFilters.AllPagesSingle);
        }
        setSelectedFilterButtons({
          type: '',
          numberOfPicBoxes: '',
          categories: '',
        });

        setApplyOptionsState({
          ...applyOptionsState,
          applyToAllPagesButton: true,
        });
        setFilterCountState({
          categoryFilterCount: 0,
          typeFilterCount: 0,
          pictureFilterCount: 0,
        });
      }
      if (applyToAllPopoutOpen) {
        setApplyToAllPopoutOpen(!applyToAllPopoutOpen);
      }
    };

    useEffect(() => {
      setSelectedLayout(defaultPageStyle);
    }, [previousPage]);

    useEffect(() => {
      if (-1 === statePageNumber) {
        return;
      }

      if (
        statePageNumber === PageConstants.FrontCoverPageNumber ||
        statePageNumber === PageConstants.BackCoverPageNumber
      ) {
        onSelectCoverTab();
      } else {
        onSelectPageTab();
      }
      return () => {
        setPreviousPage(statePageNumber);
      };
    }, [statePageNumber]);

    useEffect(() => {
      setHelpTipState({
        ...helpTipState,
        open: ui.state.pagestyleErrorTip.open,
        message: ui.state.pagestyleErrorTip.errorString,
        buttonLabel: t('*:str_ButtonOK'),
        buttonFunction: closeHelpTip,
        showAfter: 0,
        icon: <InfoIcon />,
      });
    }, [ui.state.pagestyleErrorTip.open]);

    const hasSpreadAndSingle =
      Object(pageTypes).hasOwnProperty(PageStyleType.Spread) &&
      Object(pageTypes).hasOwnProperty(PageStyleType.Single);

    const hasPageStyles =
      Object(pageTypes).hasOwnProperty(PageStyleType.Spread) ||
      Object(pageTypes).hasOwnProperty(PageStyleType.Single);

    const hasCoverPageStyles = Object(pageTypes).hasOwnProperty(PageStyleType.Cover);

    const hasCover =
      pageNumberList.indexOf(PageConstants.FrontCoverPageNumber) > -1 ||
      pageNumberList.indexOf(PageConstants.BackCoverPageNumber) > -1;

    const closeHelpTip = () => {
      if (ui.state.pagestyleErrorTip.open) {
        ui.dispatch(UserInterfaceActions.setPageStyleErrorTip({open: false}));
      }
      setHelpTipState(current => {
        return {
          ...current,
          open: false,
        };
      });
    };

    const [helpTipState, setHelpTipState] = useState<HelpTipProps>({
      open: false,
      message: '',
      buttonLabel: t('*:str_ButtonOK'),
      buttonFunction: closeHelpTip,
      showAfter: 500,
      hideAfter: 5000,
    });

    const onSelectLayout = (
      newSelectedLayout: PageStyleProps,
      detectionType?: PageDetectionActionType
    ) => {
      if (selectedLayout === newSelectedLayout) {
        setSelectedLayout(defaultPageStyle);
      } else {
        // Store the currently focused element
        const focusedElement = document.activeElement;

        setSelectedLayout(newSelectedLayout);
        app.core
          .dispatch('PAGE.APPLY_PAGE_STYLE', {
            pageNumber: statePageNumber,
            ref: newSelectedLayout.ref,
            hasGrid: newSelectedLayout.hasgrid,
            ...(detectionType && {detectionArea: detectionType}),
          })
          .then(() => {
            // Restore focus after the operation completes
            if (focusedElement instanceof HTMLElement) {
              focusedElement.focus();
            }
          })
          .catch(error => {
            if (error instanceof ApplyPageStyleError) {
              setHelpTipState({
                ...helpTipState,
                open: true,
                message: error.message,
                buttonLabel: t('*:str_ButtonOK'),
                buttonFunction: closeHelpTip,
                showAfter: 0,
                icon: <InfoIcon />,
              });
              setSelectedLayout(defaultPageStyle);
            }
            console.error(error);
          });
      }
    };

    const onSelectLayoutKeyboard = useMemo(
      () => (newSelectedLayout: PageStyleProps, event: React.KeyboardEvent) => {
        if (
          ['1', '2', '3'].includes(event.key) &&
          !event.shiftKey &&
          !event.ctrlKey &&
          !event.altKey &&
          !event.metaKey
        ) {
          // The Enter key is handled by the checkbox's native functionality and therefore triggers onSelectLayout()
          // Check if the current page is a cover
          const onCover = [
            PageConstants.FrontCoverPageNumber,
            PageConstants.BackCoverPageNumber,
          ].includes(statePageNumberRef.current);
          const isCoverStyle = newSelectedLayout.type === PageStyleType.Cover;
          const isSpreadStyle = newSelectedLayout.type === PageStyleType.Spread;
          const isValidLayout = (onCover && isCoverStyle) || (!onCover && !isCoverStyle);
          const isValidApplication =
            (isSpreadStyle && (event.key === 'Enter' || event.key === '3')) ||
            (!isSpreadStyle && (event.key === '1' || event.key === '2'));
          const hasMultiplePages = productLayout.pagecount > 1;

          let pageDetectionActionType = isSpreadStyle
            ? PageDetectionActionType.AllBoth
            : PageDetectionActionType.AllBothSeparate;
          let confirmationMessage = '';

          if (!isValidLayout || !isValidApplication) {
            // Applying a cover to a page and vice versa or invalid shortcut for the selected pagestyle
            return;
          }

          // Compile the apply to all pages command
          if (!isCoverStyle && hasMultiplePages) {
            // Apply a single-page pagestyle to all left/right pages
            if (event.key === '1' && !isSpreadStyle) {
              // Apply to all left pages
              pageDetectionActionType = PageDetectionActionType.AllLeft;
              confirmationMessage = 'str_MessageLayoutAppliedToAllLeftPages';
            }
            if (event.key === '2' && !isSpreadStyle) {
              // Apply to all right pages
              pageDetectionActionType = PageDetectionActionType.AllRight;
              confirmationMessage = 'str_MessageLayoutAppliedToAllRightPages';
            }

            if (event.key === '3') {
              // To do: This doesn't work for applying singles to all pages (core investigation required)
              // Apply either a spread or single-page pagestyle to all pages
              pageDetectionActionType = isSpreadStyle
                ? PageDetectionActionType.AllBoth
                : PageDetectionActionType.AllLeft;
              confirmationMessage = isSpreadStyle
                ? 'str_MessageLayoutAppliedToAllSpreads'
                : 'str_MessageLayoutAppliedToAllPages';
            }

            // Run the command
            onApplyToAllLayouts(
              newSelectedLayout?.ref,
              pageDetectionActionType,
              confirmationMessage
            );
          }
        }
      },
      [statePageNumber, isSpreadProduct, selectedLayout]
    );

    const filterCoverPage = (pagestyleType: number) => {
      if (selectedButtonIndex === 0) {
        return pagestyleType === PageStyleType.Cover;
      } else {
        return pagestyleType !== PageStyleType.Cover;
      }
    };

    useEffect(() => {
      if (paletteOpen && loaded && canDeviceDrag) {
        // Close the openLayoutsPalette tip if it's open
        ui.dispatch(UserInterfaceActions.setTipOpen({name: 'openLayoutsPalette', open: false}));
        // Show the layouts palette intro tip
        setIntroTipOpen(true);
      } else if (paletteOpen && !loaded) {
        setLoaded(true);
      } else if (!paletteOpen) {
        setIntroTipOpen(false);
      }

      if (!paletteOpen) {
        // reset the filter options
        setSelectedFilterButtons({
          type: '',
          numberOfPicBoxes: '',
          categories: '',
        });

        setFilterCountState({
          categoryFilterCount: 0,
          typeFilterCount: 0,
          pictureFilterCount: 0,
        });

        if (selectedButtonIndex === LayoutTypeTab.Cover) {
          setFilter(StaticFilters.AllCover);
        } else if (selectedButtonIndex === LayoutTypeTab.Page) {
          setFilter(StaticFilters.AllPages);
        }
        // Panel is closed, but there is a selected layout. Unselect it.
        if (Number(selectedLayout.ref) > 0) {
          setSelectedLayout(defaultPageStyle);
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paletteOpen, loaded]);

    useEffect(() => {
      if (1 === productLayout.spreads) {
        setIsSpreadProduct(true);
      } else {
        setIsNonSpreadProduct(true);
      }

      // Set if we can show the apply to all pages button.
      const onCover = [
        PageConstants.FrontCoverPageNumber,
        PageConstants.BackCoverPageNumber,
      ].includes(statePageNumber);

      setApplyOptionsState({
        ...applyOptionsState,
        applyToAllPagesButton: !onCover,
        applyToAllPagesPopOut: !onCover,
      });
    }, []);

    useEffect(() => {
      setSelectedLayout(defaultPageStyle);
    }, [ui.state.selectedSide.pageNumber, ui.state.selectedSide.side]);

    // Register a focusin event on #layoutPalette
    useKeyboardTipOnFocus({elementId: 'layoutPaletteContent', tipName: 'layoutspalette'});

    // Only show the content type filter if we have different pagestyle types, are on the page tab and the product is a spread.
    const showContentTypeFilter =
      perms.contentTypeFilter && hasSpreadAndSingle && productLayout.spreads === NumBool.True;

    const showPictureBoxFilter = perms.pictureBoxFilter && Object.values(allPicCounts).length > 1;
    const showCategoryFilter = perms.categoryFilter && Object.values(allCategories).length > 1;

    const hasOnePage = productLayout.pagecount === 1;

    const showFilterButton = showContentTypeFilter || showPictureBoxFilter || showCategoryFilter;

    const showApplyToAllButton =
      perms.applyToAllPagesButton && !hasOnePage && applyOptionsState.applyToAllPagesButton;

    const categoryFilterButtonAtTop: boolean = !showPictureBoxFilter && showCategoryFilter;

    const filterButton = useMemo(() => {
      return (
        showFilterButton && (
          <>
            <Button
              label={t('*:str_LabelFilter')}
              hideLabel
              startIcon={<MoreIcon />}
              buttonStyle={'standard'}
              className={'mb-xs'}
              ref={filterButtonRef}
              id={'LayoutFiltersButton'}
              onClick={() => {
                setLayoutFilterPopOutOpen(!layoutFilterPopOutOpen);
              }}
              aria-pressed={layoutFilterPopOutOpen || (totalFilterCount as unknown as number) > 0}
              size={
                ui.state.isSmallScreen &&
                experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
                  ? 'xsmall'
                  : 'small'
              }
              badgeContent={totalFilterCount > 0 ? String(totalFilterCount) : ''}
              badgeStyle={ThemeName.Primary}
              badgePosition="top-right"
            />
            <PopOut
              open={paletteOpen && layoutFilterPopOutOpen}
              id={'layoutFiltersPopOut'}
              anchorId={ui.state.isSmallScreen ? 'layoutPalette' : 'LayoutFiltersButton'}
              onClose={() => {
                setLayoutFilterPopOutOpen(false);
              }}
              excludeIdFromClickOutside={'LayoutFiltersButton'}
              repositionOnResize={ui.state.isSmallScreen}
              displayMode={'positioned'}
              offset={ui.state.isSmallScreen ? {x: 0, y: 0} : {x: 5, y: 0}}
              anchorOrigin={
                ui.state.isSmallScreen
                  ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                  : {vertical: Vertical.Top, horizontal: Horizontal.Right}
              }
              transformOrigin={
                ui.state.isSmallScreen
                  ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                  : {vertical: Vertical.Top, horizontal: Horizontal.Left}
              }
              popDirection={ui.state.isSmallScreen ? 'up' : 'right'}
              focusFirstElement={true}
              className={filtersPopoutClasses}
              theme={'tpx-theme-toolpaletteContent'}
              themeTip={'Sidebar (Content)'}
              contentTheme={ThemeName.Popouts}
            >
              {(showContentTypeFilter && LayoutTypeTab.Page === selectedButtonIndex) ||
              (showPictureBoxFilter && Object.values(picCounts).length > 1) ||
              (showCategoryFilter && Object.values(categories).length > 1) ? (
                <div className={'w-full p-sm'}>
                  {/* close button for large screen filter popout */}
                  {!ui.state.isSmallScreen && (
                    <div className={'absolute top-xs right-xs'}>
                      <Button
                        ref={closeButtonRef}
                        label={t('toolpalette:str_LabelCloseFilterPopout')}
                        hideLabel
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          setLayoutFilterPopOutOpen(false);
                          filterButtonRef.current?.focus();
                        }}
                        size={'xsmall'}
                        buttonStyle={'standard'}
                      />
                    </div>
                  )}

                  {showContentTypeFilter && LayoutTypeTab.Page === selectedButtonIndex && (
                    <>
                      <Heading level={1} className={'text-md mb-xs'} size={4}>
                        {t('boxpalettes:str_LabelPageType')}
                      </Heading>
                      <ButtonGrid>
                        <>
                          <Button
                            label={t('*:str_LabelAll')}
                            buttonStyle={'tertiary'}
                            size={'small'}
                            className={buttonCSS}
                            aria-pressed={selectedFilterButtons.type === ''}
                            onClick={() => {
                              setFilter(StaticFilters.SingleAndSpread);
                              setSelectedFilterButtons({...selectedFilterButtons, type: ''});
                              setFilterCountState({
                                ...filterCountState,
                                typeFilterCount: 0,
                              });
                            }}
                          />
                          <Button
                            key={0}
                            label={t('*:str_LabelSinglePage')}
                            buttonStyle={'tertiary'}
                            size={'small'}
                            className={buttonCSS}
                            aria-pressed={selectedFilterButtons.type === '0'}
                            onClick={() => {
                              setFilter(StaticFilters.SinglePages);
                              setSelectedFilterButtons({...selectedFilterButtons, type: '0'});
                              setFilterCountState({
                                ...filterCountState,
                                typeFilterCount: 1,
                              });
                            }}
                          />
                          <Button
                            key={1}
                            label={t('*:str_LabelSpread')}
                            buttonStyle={'tertiary'}
                            size={'small'}
                            className={buttonCSS}
                            aria-pressed={selectedFilterButtons.type === '1'}
                            onClick={() => {
                              setFilter(StaticFilters.SpreadPages);
                              setSelectedFilterButtons({...selectedFilterButtons, type: '1'});
                              setFilterCountState({
                                ...filterCountState,
                                typeFilterCount: 1,
                              });
                            }}
                          />
                        </>
                      </ButtonGrid>
                    </>
                  )}

                  {showPictureBoxFilter && Object.values(picCounts).length > 1 && (
                    <>
                      <Heading
                        level={1}
                        className={
                          selectedButtonIndex === 1 && showContentTypeFilter
                            ? 'text-md mt-lg mb-xs'
                            : 'text-md mb-xs'
                        }
                        size={4}
                      >
                        {t('toolpalette:str_TitleNumberOfPictureBoxes')}
                      </Heading>
                      <ButtonGrid>
                        <>
                          <Button
                            label={t('*:str_LabelAll')}
                            buttonStyle={'tertiary'}
                            size={'small'}
                            className={buttonCSS}
                            aria-pressed={selectedFilterButtons.numberOfPicBoxes === ''}
                            onClick={e => setAllPicBoxBtn(e)}
                          />
                          {Object.values(picCounts)
                            .filter((p: PageStyleReduced) => filterCoverPage(p.type))
                            .map((item, index) => {
                              return (
                                <Button
                                  key={index}
                                  label={item.value}
                                  buttonStyle={'tertiary'}
                                  size={'small'}
                                  className={buttonCSS}
                                  onClick={e => setPictureBoxButton(item.value, e)}
                                  aria-pressed={
                                    selectedFilterButtons.numberOfPicBoxes === item.value
                                  }
                                />
                              );
                            })}
                        </>
                      </ButtonGrid>
                    </>
                  )}

                  {showCategoryFilter && Object.values(categories).length > 1 && (
                    <>
                      <Heading
                        level={1}
                        className={
                          categoryFilterButtonAtTop ? 'text-md mb-xs' : 'text-md mt-lg mb-xs'
                        }
                        size={4}
                      >
                        {t('toolpalette:str_LabelCategories')}
                      </Heading>
                      <ButtonGrid>
                        <>
                          <Button
                            label={t('*:str_LabelAll')}
                            buttonStyle={'tertiary'}
                            size={'small'}
                            className={buttonCSS}
                            aria-pressed={selectedFilterButtons.categories === ''}
                            onClick={e => setAllCatBoxBtn(e)}
                          />
                          {Object.values(categories)
                            .filter((p: PageStyleReduced) => filterCoverPage(p.type))
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .map((item, index) => {
                              return (
                                <Button
                                  key={index}
                                  label={item.label}
                                  buttonStyle={'tertiary'}
                                  size={'small'}
                                  className={buttonCSS}
                                  onClick={e => setCategoryButton(item.value, e)}
                                  aria-pressed={selectedFilterButtons.categories === item.value}
                                />
                              );
                            })}
                        </>
                      </ButtonGrid>
                    </>
                  )}
                  {/* Show a cancel button in the popout on small screen */}
                  {ui.state.isSmallScreen && (
                    <Button
                      buttonStyle={'secondary'}
                      label={t('*:str_ButtonDone')}
                      size="medium"
                      onClick={() => {
                        setLayoutFilterPopOutOpen(false);
                      }}
                      className={'w-full mt-md'}
                    />
                  )}
                </div>
              ) : (
                <p className={'w-full p-sm'}>{t('*:str_MessageNoFiltersToShowForThisTab')}</p>
              )}
            </PopOut>
          </>
        )
      );
    }, [layoutFilterPopOutOpen, categories, picCounts, pageTypes]);

    const applyToAllButtonProps: Partial<ButtonProps> = {
      buttonStyle: 'primary',
      label:
        applyToAllPopoutOpen && applyOptionsState.applyToAllPagesButton && !ui.state.isSmallScreen
          ? t('*:str_ButtonCancel')
          : applyOptionsState.applyToAllPagesButtonLabel,
      size:
        ui.state.isSmallScreen && experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
          ? 'small'
          : 'medium',
      labelLines: 2,
      id: 'layouts-apply-to-all-button',
      disabled: applyButtonDisabled,
      className: 'flex-1',
      ref: applyToAllButtonRef,
      // 'data-keep-canvas': 'true', // To do: Check if we need this, as it will require updates to the design system
    };

    const applyToAllButton = (
      <>
        {CollectionType.Calendar === productLayout.type ? (
          <Button
            {...applyToAllButtonProps}
            aria-haspopup="dialog"
            aria-expanded={ui.state.calendarApplyLayoutsWarningDialog}
            onClick={() => ui.dispatch(UserInterfaceActions.setCalendarApplyLayoutsWarning(true))}
          />
        ) : selectedLayout.type === PageStyleType.Spread ||
          (selectedLayout.type === PageStyleType.Single && isNonSpreadProduct) ? (
          <Button {...applyToAllButtonProps} onClick={applyToAll} />
        ) : (
          <ButtonWithPopout
            key="apply-to-all-button"
            type="menu"
            open={applyToAllPopoutOpen}
            onOpen={() => setApplyToAllPopoutOpen(true)}
            onClose={() => setApplyToAllPopoutOpen(false)}
            returnFocusToTriggerOnItemClick
            buttonProps={applyToAllButtonProps}
            popoutProps={{
              // open: applyToAllPopoutOpen,
              id: 'layouts-apply-to-all-popout',
              anchorId: ui.state.isSmallScreen ? 'layoutPalette' : 'layouts-apply-to-all-button',
              onClose: () => setApplyToAllPopoutOpen(false),
              // excludeIdFromClickOutside: 'layouts-apply-to-all-button',
              ...popoutProps,
              className: applyToAllPopOutClasses,
              // focusFirstElement: true,
              theme: 'tpx-theme-toolpaletteContent',
              themeTip: 'Sidebar (Content)',
              contentTheme: ThemeName.Popouts,
            }}
          >
            <Button
              label={t('*:str_ButtonApplyToAllLeftPages')}
              buttonStyle={'standard'}
              size="medium"
              labelAlignment={'left'}
              startIcon={<BackgroundAllLeftIcon />}
              onClick={() => {
                onApplyToAllLayouts(
                  selectedLayout?.ref,
                  PageDetectionActionType.AllLeft,
                  'str_MessageLayoutAppliedToAllLeftPages'
                );
              }}
            />
            <Button
              label={t('*:str_ButtonApplyToAllRightPages')}
              buttonStyle={'standard'}
              size="medium"
              corners={'square'}
              labelAlignment={'left'}
              startIcon={<BackgroundAllRightIcon />}
              onClick={() => {
                onApplyToAllLayouts(
                  selectedLayout?.ref,
                  PageDetectionActionType.AllRight,
                  'str_MessageLayoutAppliedToAllRightPages'
                );
              }}
            />

            {/* Show a cancel button in the popout on small screen */}
            {ui.state.isSmallScreen && (
              <Button
                buttonStyle={'secondary'}
                label={t('*:str_ButtonCancel')}
                size="medium"
                onClick={toggleApplyToAllPopout}
                className={'m-sm'}
              />
            )}
          </ButtonWithPopout>
        )}
      </>
    );

    // check the experience permissions
    if (!perms.layoutsPalette) {
      return null;
    }

    // if we have no page styles, show nothing
    if (!pagestyles.length) {
      return null;
    }

    const applyLayoutTipProps: Partial<HelpTipProps> = ui.state.isSmallScreen
      ? {
          anchorId:
            ui.state.palette.section === 'pictures' ? 'picturePalette' : 'pageActionsPalette',
          anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
          transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
          offset: {x: 0, y: -10},
          popDirection: 'up',
          contentWidth: 400,
          showAfter: 1000,
        }
      : {
          anchorId: 'viewerToolbar',
          anchorOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Left},
          transformOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Left},
          offset: {x: 10, y: 0},
          popDirection: 'down',
          contentWidth: 300,
        };

    const openLayoutsTipProps: Partial<HelpTipProps> = ui.state.isSmallScreen
      ? {
          anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
          transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
          arrow: 'auto',
          offset: {x: 0, y: -10},
          showAfter: 500,
          contentWidth: 400,
        }
      : {
          icon: <ArrowLeftTopIcon />,
          anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Right},
          transformOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Left},
          offset: {x: -5, y: 5},
          contentWidth: 300,
        };

    const applyLayoutTipButtons = (
      <>
        <Button
          label={t('*:str_ButtonShowMe')}
          onClick={() => {
            if (ui.state.isSmallScreen) {
              ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
            }
            ui.dispatch(UserInterfaceActions.setTipOpen({name: 'openLayoutsPalette', open: true}));
            ui.dispatch(
              UserInterfaceActions.setTipOpen({
                name: 'arrangePicturesByApplyingLayout',
                open: false,
              })
            );
          }}
          buttonStyle={'primary'}
          size={'small'}
        />
        <Button
          label={t('*:str_ButtonClose')}
          onClick={() =>
            ui.dispatch(
              UserInterfaceActions.setTipOpen({
                name: 'arrangePicturesByApplyingLayout',
                open: false,
              })
            )
          }
          buttonStyle={'secondary'}
          size={'small'}
        />
      </>
    );

    const tabs = [
      {id: 'coversTab', label: t('*:str_LabelCovers'), onClick: onSelectCoverTab},
      {id: 'pagesTab', label: t('*:str_LabelPages'), onClick: onSelectPageTab},
    ];

    return (
      <>
        <ToolPaletteSection
          sectionKey={sectionKey}
          useFocusMode
          buttonLabel={t('*:str_LabelLayouts')}
          title={t('*:str_LabelLayouts')}
          buttonIcon={<LayoutIcon />}
          id={'layoutPalette'}
          ref={ref}
          showSeparator={false}
          onToggle={() => {
            setApplyToAllPopoutOpen(false);
          }}
        >
          <div className={contentWrapperClasses} ref={contentRef} id="layoutPaletteContent">
            {hasCover && (hasCoverPageStyles || hasPageStyles) ? (
              // Only show the tabs if there is a cover and at least one of the tabs would have page styles in it..
              <TabList
                id="tabsContainer"
                tabpanelId="assetListContainer"
                orientation="horizontal"
                aria-label={t('toolpalette:str_LabelLayoutTypes', {defaultValue: 'Layout Types'})}
                className={tabsContainerClasses}
              >
                {tabs.map((tab, index) => {
                  return (
                    <Button
                      id={tab.id}
                      role="tab"
                      aria-selected={selectedButtonIndex === index}
                      aria-controls="assetListContainer"
                      label={tab.label}
                      buttonStyle={'tab'}
                      tabPosition={'top'}
                      size={
                        ui.state.isSmallScreen &&
                        experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
                          ? 'xsmall'
                          : 'small'
                      }
                      onClick={tab.onClick}
                      className={'flex-1' + (selectedButtonIndex === index ? ' -mb-[1rem]' : '')}
                      labelAlignmentWhenWrapped="left"
                    />
                  );
                })}
                {showFilterButton && <>{filterButton}</>}
              </TabList>
            ) : (
              // If there is no cover, just show the filter button at the top of the page layouts list.
              <>
                {showFilterButton && <div className={'flex justify-end px-md'}>{filterButton}</div>}
              </>
            )}

            <div
              id="assetListContainer"
              className={thumbnailScrollContainerClasses}
              role="tabpanel"
              aria-labelledby={selectedButtonIndex !== -1 ? tabs[selectedButtonIndex].id : ''}
            >
              <LayoutsList
                visible={paletteOpen}
                pagestyles={filteredPageStyles}
                selectedLayout={selectedLayout}
                onClick={(newSelectedLayout: PageStyleProps) => {
                  onSelectLayout(newSelectedLayout);
                }}
                onKeyDown={(newSelectedLayout: PageStyleProps, event: React.KeyboardEvent) => {
                  onSelectLayoutKeyboard(newSelectedLayout, event);
                }}
                layoutType={1 === selectedButtonIndex ? 'page' : 'cover'}
              />
            </div>

            {showApplyToAllButton && (
              <div id="bottomButtonsContainer" className={bottomButtonsContainerClasses}>
                {applyToAllButton}
              </div>
            )}
          </div>

          <HelpTip
            id={'layoutsIntroTip'}
            open={!ui.state.isSmallScreen && introTipOpen}
            message={t('str_MessageHowToUseLayoutsPalette', {
              ns: 'helptips',
            })}
            buttonLabel={t('*:str_ButtonOK')}
            buttonFunction={() => setIntroTipOpen(false)}
            anchorId={'assetListContainer'}
            anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
            transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
            offset={{x: 10, y: 0}}
            icon={<ArrowLeftTopIcon />}
            className={'max-w-[240rem]'}
            showAfter={500}
            hideAfter={5000}
            onClose={() => setIntroTipOpen(false)}
            showMode={'once'}
          />

          <HelpTip
            {...helpTipState}
            id={'genericLayoutsMessageTip'}
            anchorId={'viewerToolbar'}
            anchorOrigin={
              ui.state.isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Bottom, horizontal: Horizontal.Left}
            }
            transformOrigin={
              ui.state.isSmallScreen
                ? {vertical: Vertical.Top, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Left}
            }
            offset={ui.state.isSmallScreen ? undefined : {x: 10, y: 0}}
            showMode={'multiple'}
            onClose={() => {
              closeHelpTip();
            }}
          />
        </ToolPaletteSection>
        <ApplyToAllWarningDialog
          open={ui.state.calendarApplyLayoutsWarningDialog}
          onCancel={() => {
            ui.dispatch(UserInterfaceActions.setCalendarApplyLayoutsWarning(false));
          }}
          onContinue={() => {
            ui.dispatch(UserInterfaceActions.setCalendarApplyLayoutsWarning(false));
            onApplyToAllLayouts(
              selectedLayout?.ref,
              isSpreadProduct ? PageDetectionActionType.AllBoth : PageDetectionActionType.AllLeft,
              isSpreadProduct
                ? 'str_MessageLayoutAppliedToAllSpreads'
                : 'str_MessageLayoutAppliedToAllPages'
            );
          }}
        />
        <HelpTip
          open={ui.state.tips.arrangePicturesByApplyingLayout}
          id={'applyLayoutAfterAddingPicture'}
          message={t('helptips:str_MessageArrangePicturesByApplyingLayout')}
          title={t('*:str_TitleTip')}
          titleIcon={<LightBulbIcon />}
          showMode="once"
          {...applyLayoutTipProps}
          customButtons={applyLayoutTipButtons}
        />
        <HelpTip
          open={ui.state.tips.openLayoutsPalette}
          id={'openLayoutsPalette'}
          message={t('helptips:str_MessageOpenTheLayoutsTabAndSelectALayout')}
          anchorId={'toolPaletteButton-layoutPalette'}
          buttonLabel={t('*:str_ButtonOK')}
          buttonFunction={() => {
            ui.dispatch(UserInterfaceActions.setTipOpen({name: 'openLayoutsPalette', open: false}));
          }}
          {...openLayoutsTipProps}
        />
      </>
    );
  }
);

export default LayoutsPalette;
